import {
    Box,
    Button,
    Chip,
    Container,
    Grid,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import AddCardOutlined from '@mui/icons-material/AddCardOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { Link } from 'react-router-dom';
import { useFetch } from '../../../hooks/useFecth';
import MetricCard from '../components/MetricCard';

const SECTIONS = [
    { id: 'fluxo-facil', label: 'Fluxo fácil' },
    { id: 'previsto-realizado', label: 'Previsto × Realizado' },
    { id: 'agenda', label: 'Agenda do mês' },
    { id: 'titulos-atrasados', label: 'Títulos atrasados' },
    { id: 'fluxo-caixa', label: 'Gastos por slot' },
    { id: 'comparativos', label: 'Comparativos' },
];

const TITLE_KIND = {
    parcelado: { label: 'Parcelado', color: 'info' },
    recorrente: { label: 'Recorrente', color: 'primary' },
    avulso: { label: 'Avulso', color: 'default' },
};

const TITLE_STATUS = {
    OVERDUE: { label: 'Atrasada', color: 'error' },
    PENDING: { label: 'Pendente vencida', color: 'warning' },
    WAITING: { label: 'Aguardando vencida', color: 'warning' },
    AWAITING_RISK_ANALYSIS: { label: 'Em análise vencida', color: 'warning' },
    RECEIVED: { label: 'Paga', color: 'success' },
    CONFIRMED: { label: 'Confirmada', color: 'success' },
};

const paperSx = {
    p: 3,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: 'none',
    scrollMarginTop: 96,
};

function money(value) {
    return `R$ ${(Number(value) || 0).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function formatDay(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('pt-BR');
}

function deltaLabel(atual, anterior) {
    const diff = Number(atual || 0) - Number(anterior || 0);
    if (!anterior) return diff > 0 ? 'Novo no período' : 'Sem base anterior';
    const pct = (diff / Math.abs(anterior)) * 100;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${pct.toFixed(0)}% vs mês anterior`;
}

function Section({ id, title, subtitle, children }) {
    return (
        <Paper id={id} sx={{ ...paperSx, mb: 3 }}>
            <Typography variant="h6" fontWeight={600}>
                {title}
            </Typography>
            {subtitle ? (
                <Typography variant="body2" color="text.secondary" mb={2}>
                    {subtitle}
                </Typography>
            ) : (
                <Box mb={2} />
            )}
            {children}
        </Paper>
    );
}

function TitlesTable({ rows }) {
    if (!rows.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                Nenhum título neste recorte.
            </Typography>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Clube</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(title => {
                    const kind = TITLE_KIND[title.kind] || TITLE_KIND.avulso;
                    const status = TITLE_STATUS[title.status] || { label: title.status || '—', color: 'default' };
                    return (
                        <TableRow key={title.id} hover>
                            <TableCell>
                                {title.clubId ? (
                                    <Typography
                                        component={Link}
                                        to={`/dashboard/clubes/${title.clubId}`}
                                        variant="body2"
                                        fontWeight={600}
                                        sx={{ color: 'primary.main', textDecoration: 'none' }}
                                    >
                                        {title.clubName}
                                    </Typography>
                                ) : (
                                    <Typography variant="body2">{title.clubName}</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                {title.description || '—'}
                                {title.installmentNumber ? ` · parcela ${title.installmentNumber}` : ''}
                            </TableCell>
                            <TableCell>
                                <Chip size="small" color={kind.color} label={kind.label} />
                            </TableCell>
                            <TableCell>{formatDay(title.dueDate)}</TableCell>
                            <TableCell>
                                <Chip size="small" color={status.color} label={status.label} />
                            </TableCell>
                            <TableCell align="right">{money(title.value)}</TableCell>
                            <TableCell align="right">
                                {title.invoiceUrl ? (
                                    <Button size="small" component="a" href={title.invoiceUrl} target="_blank" rel="noreferrer">
                                        Boleto
                                    </Button>
                                ) : null}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
}

export default function FinanceOverview() {
    const { data: stats } = useFetch('finance/stats', { params: { period: 'semestral' } });
    const [active, setActive] = useState(SECTIONS[0].id);
    const [selectedDay, setSelectedDay] = useState(null);

    const previsto = stats?.previstoRealizado?.previsto || {};
    const realizado = stats?.previstoRealizado?.realizado || {};
    const agenda = stats?.agenda || [];
    const titles = stats?.titulosAtrasados || [];
    const slots = stats?.gastosPorSlot || [];
    const fluxo = stats?.fluxoSemestre || [];
    const comparativos = stats?.comparativos || {};

    const currentDay = useMemo(() => agenda.find(day => day.current) || agenda[3] || null, [agenda]);
    const dayKey = selectedDay || currentDay?.key;
    const dayItems = agenda.find(day => day.key === dayKey)?.itens || [];

    useEffect(() => {
        const nodes = SECTIONS.map(section => document.getElementById(section.id)).filter(Boolean);
        if (!nodes.length) return undefined;
        const observer = new IntersectionObserver(
            entries => {
                const visible = entries
                    .filter(entry => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
                if (visible?.target?.id) setActive(visible.target.id);
            },
            { rootMargin: '-20% 0px -55% 0px', threshold: [0.15, 0.4, 0.7] },
        );
        nodes.forEach(node => observer.observe(node));
        return () => observer.disconnect();
    }, []);

    const scrollTo = id => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActive(id);
    };

    return (
        <Box>
            <Container maxWidth="xl">
                <Stack direction={{ xs: 'column', lg: 'row' }} alignItems="flex-start" gap={3}>
                    <Box sx={{ flex: 1, minWidth: 0, pb: 6 }}>
                        <Stack pb={3}>
                            <Typography variant="h2" gutterBottom>
                                Visão geral
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Fluxo do mês, agenda da semana corrente e títulos em aberto.
                            </Typography>
                        </Stack>

                        <Section
                            id="fluxo-facil"
                            title="Faça lançamentos no Fluxo fácil"
                            subtitle="Atalho para os fluxos que já geram títulos: assinaturas dos clubes e cobrança de benefícios."
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to="/dashboard/assinaturas"
                                    startIcon={<AddCardOutlined />}
                                >
                                    Lançar assinatura
                                </Button>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/dashboard/beneficios"
                                    startIcon={<ReceiptLongOutlined />}
                                >
                                    Cobrança de benefícios
                                </Button>
                            </Stack>
                        </Section>

                        <Section
                            id="previsto-realizado"
                            title="Saldo previsto × realizado"
                            subtitle="Previsto pelos vencimentos deste mês. Realizado pelas invoices pagas neste mês."
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <MetricCard
                                        title="Previsto"
                                        value={money(previsto.total)}
                                        subtitle={`${(previsto.quantidade || 0).toLocaleString('pt-BR')} títulos com vencimento no mês`}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <MetricCard
                                        title="Realizado"
                                        value={money(realizado.total)}
                                        subtitle={`${(realizado.quantidade || 0).toLocaleString('pt-BR')} invoices pagas no mês`}
                                    />
                                </Grid>
                            </Grid>
                        </Section>

                        <Section
                            id="agenda"
                            title="Agenda do mês"
                            subtitle="Sete dias com o dia de hoje no centro. Clique em um dia para ver os títulos."
                        >
                            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
                                {agenda.map(day => {
                                    const selected = day.key === dayKey;
                                    return (
                                        <Paper
                                            key={day.key}
                                            onClick={() => setSelectedDay(day.key)}
                                            sx={{
                                                flex: '1 1 0',
                                                minWidth: 88,
                                                p: 1.5,
                                                cursor: 'pointer',
                                                textAlign: 'center',
                                                border: '1px solid',
                                                borderColor: selected ? 'primary.main' : 'divider',
                                                bgcolor: day.current ? 'primary.main' : selected ? 'action.selected' : 'background.paper',
                                                color: day.current ? 'primary.contrastText' : 'text.primary',
                                                boxShadow: 'none',
                                            }}
                                        >
                                            <Typography variant="caption" sx={{ textTransform: 'capitalize', opacity: 0.8 }}>
                                                {day.weekday}
                                            </Typography>
                                            <Typography variant="h5" fontWeight={700}>
                                                {day.day}
                                            </Typography>
                                            <Typography variant="caption">
                                                {day.quantidade ? `${day.quantidade} título(s)` : '—'}
                                            </Typography>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                            <Box mt={2}>
                                <TitlesTable rows={dayItems} />
                            </Box>
                        </Section>

                        <Section
                            id="titulos-atrasados"
                            title="Títulos atrasados"
                            subtitle="Inclui boletos em atraso e pendentes já vencidos — também parcelados e recorrentes em andamento."
                        >
                            <TitlesTable rows={titles} />
                        </Section>

                        <Section
                            id="fluxo-caixa"
                            title="Gastos por slot · Fluxo de caixa do semestre"
                            subtitle="Agrupamento pelos cortes 10, 20 e último dia. O gráfico compara previsto e realizado nos últimos 6 meses."
                        >
                            <Grid container spacing={2} mb={3}>
                                {slots.map(slot => (
                                    <Grid item xs={12} md={4} key={slot.slot}>
                                        <MetricCard
                                            title={slot.label}
                                            value={money(slot.previsto)}
                                            subtitle={`Realizado ${money(slot.realizado)} · ${slot.quantidade} título(s)`}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={fluxo}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 8 }}
                                        formatter={value => money(value)}
                                    />
                                    <Bar dataKey="previsto" name="Previsto" fill="#90CAF9" radius={[4, 4, 0, 0]} />
                                    <Bar dataKey="realizado" name="Realizado" fill="#00D4AA" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Section>

                        <Section id="comparativos" title="Comparativos" subtitle="Este mês contra o mês anterior.">
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <MetricCard
                                        title="Previsto"
                                        value={money(comparativos.previsto?.atual)}
                                        subtitle={deltaLabel(comparativos.previsto?.atual, comparativos.previsto?.anterior)}
                                        trend={
                                            Number(comparativos.previsto?.atual || 0) >=
                                            Number(comparativos.previsto?.anterior || 0)
                                                ? 'up'
                                                : 'down'
                                        }
                                        trendValue={money(comparativos.previsto?.anterior)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MetricCard
                                        title="Realizado"
                                        value={money(comparativos.realizado?.atual)}
                                        subtitle={deltaLabel(comparativos.realizado?.atual, comparativos.realizado?.anterior)}
                                        trend={
                                            Number(comparativos.realizado?.atual || 0) >=
                                            Number(comparativos.realizado?.anterior || 0)
                                                ? 'up'
                                                : 'down'
                                        }
                                        trendValue={money(comparativos.realizado?.anterior)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MetricCard
                                        title="Títulos atrasados"
                                        value={comparativos.atrasados?.atual || 0}
                                        subtitle={money(comparativos.atrasados?.total)}
                                    />
                                </Grid>
                            </Grid>
                        </Section>
                    </Box>

                    <Box
                        sx={{
                            width: 220,
                            flexShrink: 0,
                            position: 'sticky',
                            top: 88,
                            display: { xs: 'none', lg: 'block' },
                        }}
                    >
                        <Paper sx={{ ...paperSx, p: 1.5 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ px: 1.5 }}>
                                Nesta página
                            </Typography>
                            {SECTIONS.map(section => (
                                <ListItemButton
                                    key={section.id}
                                    selected={active === section.id}
                                    onClick={() => scrollTo(section.id)}
                                    sx={{ borderRadius: 1, py: 0.75 }}
                                >
                                    <ListItemText
                                        primary={section.label}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: active === section.id ? 700 : 500 }}
                                    />
                                </ListItemButton>
                            ))}
                        </Paper>
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
}
